import React, { createContext, useContext, useState, useCallback, useEffect } from "react"

// Simple class merger
function cn(...classes) {
  return classes.filter(Boolean).join(" ")
}

const SidebarContext = createContext(null)

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider")
  }
  return context
}

export function SidebarProvider({
  children,
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  ...props
}) {
  // Use a cookie or local storage to persist state if needed, but for now just state
  const [openState, setOpenState] = useState(defaultOpen)

  // Persist to localStorage
  useEffect(() => {
    // On mobile we want the sidebar hidden by default (even if it was opened on desktop).
    // Tailwind `md` breakpoint is 768px, so mobile is < 768.
    const isMobile = typeof window !== 'undefined'
      && typeof window.matchMedia !== 'undefined'
      && window.matchMedia('(max-width: 767px)').matches

    if (isMobile) {
      setOpenState(false)
      return
    }

    const savedState = localStorage.getItem('sidebar:state')
    if (savedState !== null) {
      setOpenState(savedState === 'true')
    }
  }, [])

  const open = openProp !== undefined ? openProp : openState
  const setOpen = useCallback(
    (value) => {
      const newState = typeof value === "function" ? value(open) : value
      if (setOpenProp) {
        setOpenProp(newState)
      } else {
        setOpenState(newState)
        localStorage.setItem('sidebar:state', String(newState))
      }
    },
    [setOpenProp, open]
  )

  const toggleSidebar = useCallback(() => {
    setOpen((prev) => !prev)
  }, [setOpen])

  const state = open ? "expanded" : "collapsed"

  return (
    <SidebarContext.Provider
      value={{
        state,
        open,
        setOpen,
        toggleSidebar,
      }}
    >
      <div
        style={{
          "--sidebar-width": "18rem", // 288px
          ...style,
        }}
        className={cn(
          "group/sidebar-wrapper flex min-h-screen w-full has-[[data-variant=inset]]:bg-sidebar",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  )
}

export function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}) {
  const { open, state, setOpen } = useSidebar()

  const isLeft = side === 'left'
  const translateClosed = isLeft ? "-translate-x-full" : "translate-x-full"

  return (
    <>
      {/* Mobile: off-canvas drawer (overlays the content) */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/40 md:hidden transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        aria-hidden={!open}
        onClick={() => setOpen(false)}
      />

      <div
        className={cn(
          "fixed inset-y-0 z-50 w-[--sidebar-width] md:hidden transform-gpu transition-transform duration-200 ease-out bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800",
          isLeft ? "left-0" : "right-0",
          open ? "translate-x-0" : translateClosed,
          className
        )}
        data-state={state}
        data-collapsible={state === "collapsed" ? collapsible : ""}
        data-variant={variant}
        data-side={side}
        {...props}
      >
        {/* Mobile close button (like a modal) */}
        <button
          type="button"
          aria-label="Close sidebar"
          onClick={(e) => {
            e.stopPropagation()
            setOpen(false)
          }}
          className={cn(
            "absolute top-3 right-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full",
            "bg-white/90 text-gray-700 shadow-md ring-1 ring-black/10 hover:bg-white",
            "dark:bg-gray-800/90 dark:text-gray-100 dark:ring-white/10 dark:hover:bg-gray-800"
          )}
        >
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>

        <div
          data-sidebar="sidebar"
          className="flex h-full w-full flex-col bg-sidebar"
        >
          {children}
        </div>
      </div>

      {/* Desktop: sticky sidebar */}
      <div
        className={cn(
          "sticky top-0 z-50 hidden h-screen w-[--sidebar-width] transition-[width] duration-200 ease-linear md:flex flex-col bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800",
          !open && "w-0 border-none overflow-hidden",
          className
        )}
        data-state={state}
        data-collapsible={state === "collapsed" ? collapsible : ""}
        data-variant={variant}
        data-side={side}
      >
        <div
          data-sidebar="sidebar"
          className={cn(
            "flex h-full w-full flex-col bg-sidebar",
            // If closed, we might want to hide content to prevent it from showing while animating width
            !open && "invisible"
          )}
        >
          {children}
        </div>
      </div>
    </>
  )
}

export function SidebarTrigger({ className, onClick, ...props }) {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      data-sidebar="trigger"
      className={cn("h-7 w-7 flex items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-gray-800", className)}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth={2} />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v18" />
      </svg>
      <span className="sr-only">Toggle Sidebar</span>
    </button>
  )
}

export function SidebarContent({ className, ...props }) {
  return (
    <div
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className
      )}
      {...props}
    />
  )
}

export function SidebarHeader({ className, ...props }) {
  return (
    <div
      data-sidebar="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
}

export function SidebarFooter({ className, ...props }) {
  return (
    <div
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
}
